import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import App from './App';
import { createAppTheme, createEmbeddedAppTheme } from './theme';
import { queryClient } from './lib/queryClient';
import { QueryDevtools } from './lib/QueryDevtools';
import { useAuthStore } from './store/auth';
import { EmbeddedThemeBridge } from './components/EmbeddedThemeBridge';
import { FrameViewportSync } from './components/FrameViewportSync';
import { readBootstrapEmbedTheme } from './lib/bootstrapEmbedTheme';
import { captureSignupAttribution } from './lib/attribution';

const bootstrapEmbedTheme = readBootstrapEmbedTheme();
// Campaign parameters handed over by landing-page signup links (/register?utm_source=...).
captureSignupAttribution();

function ThemedApp() {
  const isEmbedded = useAuthStore((state) => state.isEmbedded);
  const embedTheme = useAuthStore((state) => state.embedContext?.theme);
  const effectiveEmbedTheme = embedTheme ?? bootstrapEmbedTheme;
  const theme = React.useMemo(
    () => (isEmbedded || bootstrapEmbedTheme) && effectiveEmbedTheme
      ? createEmbeddedAppTheme(effectiveEmbedTheme)
      : createAppTheme('light'),
    [effectiveEmbedTheme, isEmbedded],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <EmbeddedThemeBridge />
      <FrameViewportSync />
      <App />
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
        <QueryDevtools />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
